import { useEffect, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import { ChevronLeft, ChevronRight, Download, Minus, Plus, RotateCcw, RotateCw, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { clampZoom, getImageDownloadProps, getLightboxKeyAction, isBackdropClick, nextImageIndex } from './lightboxLogic'

type ImageItem = {
  name: string
  url: string
}

type ImageLightboxProps = {
  images: ImageItem[]
  index: number
  onClose: () => void
  onChange: (index: number) => void
}

type ImageViewState = {
  index: number
  zoom: number
  rotation: number
  pan: { x: number; y: number }
}

export function ImageLightbox({ images, index, onClose, onChange }: ImageLightboxProps) {
  const [view, setView] = useState<ImageViewState>({
    index,
    zoom: 1,
    rotation: 0,
    pan: { x: 0, y: 0 },
  })
  const [isDragging, setIsDragging] = useState(false)
  const dragOrigin = useRef({ x: 0, y: 0 })
  const dialogRef = useRef<HTMLDivElement>(null)
  const image = images[index]
  const isCurrentView = view.index === index
  const zoom = isCurrentView ? view.zoom : 1
  const rotation = isCurrentView ? view.rotation : 0
  const pan = isCurrentView ? view.pan : { x: 0, y: 0 }

  function updateView(patch: Partial<Omit<ImageViewState, 'index'>>) {
    setView((current) => ({
      index,
      zoom: current.index === index ? current.zoom : 1,
      rotation: current.index === index ? current.rotation : 0,
      pan: current.index === index ? current.pan : { x: 0, y: 0 },
      ...patch,
    }))
  }

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      const action = getLightboxKeyAction(event.key, index, images.length)
      if (action?.type === 'close') onClose()
      if (action?.type === 'change') onChange(action.index)

      if (event.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>('button, a')
        if (!focusableElements?.length) return
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      previousFocus?.focus()
    }
  }, [images.length, index, onChange, onClose])

  if (!image) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 text-white"
      role="presentation"
      onMouseDown={(event) => {
        if (isBackdropClick(event.target, event.currentTarget)) onClose()
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Xem ảnh ${image.name}`}
        className="flex h-full min-h-0 flex-col outline-none"
      >
        <ImageViewerToolbar
          image={image}
          index={index}
          imageCount={images.length}
          zoom={zoom}
          onZoomChange={(value) => updateView({ zoom: value })}
          onRotationChange={(delta) => updateView({ rotation: rotation + delta })}
          onClose={onClose}
        />
        <ImageViewerCanvas
          image={image}
          index={index}
          imageCount={images.length}
          zoom={zoom}
          rotation={rotation}
          pan={pan}
          isDragging={isDragging}
          dragOrigin={dragOrigin}
          onZoomChange={(value) => updateView({ zoom: value })}
          onPanChange={(value) => updateView({ pan: value })}
          onDraggingChange={setIsDragging}
          onChange={onChange}
        />
      </div>
    </div>
  )
}

function ImageViewerToolbar({
  image,
  index,
  imageCount,
  zoom,
  onZoomChange,
  onRotationChange,
  onClose,
}: {
  image: ImageItem
  index: number
  imageCount: number
  zoom: number
  onZoomChange: (zoom: number) => void
  onRotationChange: (delta: number) => void
  onClose: () => void
}) {
  return (
    <header className="flex items-center justify-between gap-3 p-3">
      <p className="min-w-0 truncate text-sm">{image.name} · {index + 1}/{imageCount}</p>
      <div className="flex items-center gap-1">
        <Button variant="secondary" size="sm" aria-label="Thu nhỏ" onClick={() => onZoomChange(clampZoom(zoom - 0.25))}>
          <Minus className="h-4 w-4" />
        </Button>
        <span className="min-w-12 text-center text-xs">{Math.round(zoom * 100)}%</span>
        <Button variant="secondary" size="sm" aria-label="Phóng to" onClick={() => onZoomChange(clampZoom(zoom + 0.25))}>
          <Plus className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="sm" aria-label="Xoay trái" onClick={() => onRotationChange(-90)}>
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="sm" aria-label="Xoay phải" onClick={() => onRotationChange(90)}>
          <RotateCw className="h-4 w-4" />
        </Button>
        <a className="rounded px-2 py-2 hover:bg-white/10" {...getImageDownloadProps(image.url, image.name)} aria-label="Tải ảnh xuống">
          <Download className="h-4 w-4" />
        </a>
        <Button variant="secondary" size="sm" aria-label="Đóng" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </header>
  )
}

function ImageViewerCanvas({
  image,
  index,
  imageCount,
  zoom,
  rotation,
  pan,
  isDragging,
  dragOrigin,
  onZoomChange,
  onPanChange,
  onDraggingChange,
  onChange,
}: {
  image: ImageItem
  index: number
  imageCount: number
  zoom: number
  rotation: number
  pan: { x: number; y: number }
  isDragging: boolean
  dragOrigin: MutableRefObject<{ x: number; y: number }>
  onZoomChange: (zoom: number) => void
  onPanChange: (pan: { x: number; y: number }) => void
  onDraggingChange: (dragging: boolean) => void
  onChange: (index: number) => void
}) {
  return (
    <main
      className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
      onWheel={(event) => {
        event.preventDefault()
        onZoomChange(clampZoom(zoom + (event.deltaY < 0 ? 0.1 : -0.1)))
      }}
      onMouseMove={(event) => {
        if (isDragging) {
          onPanChange({
            x: event.clientX - dragOrigin.current.x,
            y: event.clientY - dragOrigin.current.y,
          })
        }
      }}
      onMouseUp={() => onDraggingChange(false)}
      onMouseLeave={() => onDraggingChange(false)}
      onMouseDown={(event) => {
        if (zoom > 1) {
          dragOrigin.current = { x: event.clientX - pan.x, y: event.clientY - pan.y }
          onDraggingChange(true)
        }
      }}
    >
      <button
        type="button"
        className="absolute left-3 z-10 rounded-full bg-black/50 p-2"
        aria-label="Ảnh trước"
        onClick={() => onChange(nextImageIndex(index, -1, imageCount))}
      >
        <ChevronLeft />
      </button>
      <img
        src={image.url}
        alt={image.name}
        draggable={false}
        className="max-h-full max-w-full select-none object-contain"
        style={{
          transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
      />
      <button
        type="button"
        className="absolute right-3 z-10 rounded-full bg-black/50 p-2"
        aria-label="Ảnh sau"
        onClick={() => onChange(nextImageIndex(index, 1, imageCount))}
      >
        <ChevronRight />
      </button>
    </main>
  )
}
